import { useEffect, useLayoutEffect, useState } from "react";

/**
 * The candidate app's light / dark theme — one key, two switches (UI audit 2026-10-16, docs/30 D7).
 *
 * The tokens (ui/tokens.css) turn dark on `.ux-root[data-theme="dark"]`; Tailwind's `dark:` utilities
 * (darkMode: "class") turn dark under `html.dark`. They used to be driven by two different keys: the top
 * bar's moon wrote "ux-theme" and set only data-theme, while `html.dark` came from "theme", read once on
 * load and written by Account settings. The moon gave dark surfaces with light badges and white exam cards.
 *
 * Now: one key, "ux-theme". Every writer goes through setStoredTheme(), which stores it, sets `html.dark`
 * and tells every mounted shell (data-theme follows through useTheme()). A value left under the old key
 * is read once, carried over, and removed.
 */
export const THEME_KEY = "ux-theme";
const LEGACY_KEY = "theme";
export const THEME_EVENT = "ux-theme-change";

const normalise = (v) => (v === "dark" ? "dark" : "light");

export function readTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v) return normalise(v);
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      localStorage.setItem(THEME_KEY, normalise(legacy));
      localStorage.removeItem(LEGACY_KEY);
      return normalise(legacy);
    }
  } catch { /* storage blocked: light */ }
  return "light";
}

/** `html.dark` for the dark: utilities (and the page background in tokens.css). */
export function applyTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", normalise(theme) === "dark");
}

export function setStoredTheme(theme) {
  const next = normalise(theme);
  try {
    localStorage.setItem(THEME_KEY, next);
    localStorage.removeItem(LEGACY_KEY);
  } catch { /* storage blocked: this visit only */ }
  applyTheme(next);
  try { window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { theme: next } })); } catch { /* old browser */ }
  return next;
}

/** [theme, setTheme, toggle] — kept in step with every other writer and with other tabs. */
export function useTheme() {
  const [theme, setThemeState] = useState(readTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const onStorage = (e) => { if (!e || e.key === null || e.key === THEME_KEY) setThemeState(readTheme()); };
    const onChange = (e) => setThemeState(normalise(e?.detail?.theme || readTheme()));
    window.addEventListener("storage", onStorage);
    window.addEventListener(THEME_EVENT, onChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(THEME_EVENT, onChange);
    };
  }, []);

  const setTheme = (t) => setThemeState(setStoredTheme(t));
  const toggle = () => setTheme(theme === "dark" ? "light" : "dark");
  return [theme, setTheme, toggle];
}

/**
 * The exam-day screens (lobby, pre-checks, the paper, worksheet scan, the result, the phone pages) are
 * light only: they are drawn in fixed colours a candidate must be able to read under any setting, and
 * the paper is the hot path — no restyling there. While one is mounted `html.dark` is off; leaving it
 * puts the stored theme back.
 */
export function LightOnly({ children }) {
  useLayoutEffect(() => {
    applyTheme("light");
    return () => applyTheme(readTheme());
  }, []);
  return children;
}
